import React, { useState, useEffect, useCallback } from "react";
import { Landmark, Save, Loader2, RefreshCw } from "lucide-react";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const CreditReserve = ({ client }) => {
  const [s, setS] = useState(null);
  const [total, setTotal] = useState("");
  const [pct, setPct] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await client.get("/admin/reserve");
      setS(r.data);
      setTotal(String(r.data.total_reserve));
      setPct(String((r.data.ratio * 100).toFixed(2)));
    } catch { /* noop */ }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await client.post("/admin/reserve", { total_reserve: Number(total), ratio: Number(pct) / 100 });
      setS(r.data);
    } catch { /* noop */ }
    setSaving(false);
  };

  const util = s ? Math.min(100, Math.round(s.utilization * 100)) : 0;
  const Stat = ({ label, value, accent }) => (
    <div className="bg-[#0E0E0A] border border-[#2A2A22] rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">{label}</div>
      <div className={`text-lg font-mono mt-1 ${accent || "text-[#ECECE4]"}`}>{value}</div>
    </div>
  );

  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-xl p-5" data-testid="reserve-panel">
      {!s ? <Loader2 className="animate-spin text-[#8A8A8A]" /> : (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <Stat label="Total Reserve" value={usd(s.total_reserve)} accent="text-[#3DBB8E]" />
            <Stat label="Backing Capacity" value={usd(s.capacity)} accent="text-[#9DB4E8]" />
            <Stat label="Reserve Ratio" value={`${(s.ratio * 100).toFixed(1)}%`} />
            <Stat label="Committed (outstanding)" value={usd(s.committed)} accent="text-[#E4C97A]" />
            <Stat label="Available Headroom" value={usd(s.available)} accent={s.available < 0 ? "text-[#FF8585]" : "text-[#3DBB8E]"} />
            <Stat label="Outstanding Drafts" value={s.outstanding_count} />
          </div>

          <div className="mb-1 flex items-center justify-between text-[11px] text-[#8A8A8A]">
            <span>Utilization</span><span data-testid="reserve-utilization">{util}%</span>
          </div>
          <div className="w-full h-2.5 bg-[#0E0E0A] border border-[#2A2A22] rounded-full overflow-hidden mb-6">
            <div className="h-full rounded-full" data-testid="reserve-util-bar"
              style={{ width: `${util}%`, background: util > 90 ? "#FF8585" : util > 70 ? "#E4C97A" : "#0B6B4F" }} />
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1 text-[11px]">
              <span className="uppercase tracking-[0.1em] text-[#8A8A8A]">Total Reserve (USD)</span>
              <input value={total} onChange={(e) => setTotal(e.target.value)} data-testid="reserve-total-input"
                className="bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px] w-48" />
            </label>
            <label className="flex flex-col gap-1 text-[11px]">
              <span className="uppercase tracking-[0.1em] text-[#8A8A8A]">Reserve Ratio (%)</span>
              <input value={pct} onChange={(e) => setPct(e.target.value)} data-testid="reserve-ratio-input"
                className="bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px] w-28" />
            </label>
            <button onClick={save} disabled={saving} data-testid="reserve-save-btn"
              className="inline-flex items-center gap-2 bg-[#0B6B4F] hover:bg-[#0a5a43] disabled:opacity-50 text-white font-semibold text-[12px] uppercase tracking-[0.1em] px-4 py-2 rounded-md">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
            <button onClick={load} className="text-[#8A8A8A] hover:text-[#ECECE4] mb-1" data-testid="reserve-refresh-btn"><RefreshCw size={14} /></button>
          </div>
          <p className="mt-3 text-[10px] text-[#5C5C5C]">Each newly vaulted draft commits its face value against capacity; redeeming or voiding a draft releases it. New issuance exceeding available headroom is blocked.</p>
        </>
      )}
    </div>
  );
};
